import React, { useState } from 'react';
import { Archive, Check, CreditCard, Megaphone, MessageCircle, RotateCcw, ShoppingBag, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, formatApiError } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) { resolve(true); return; }
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => reject(new Error('Razorpay failed to load'));
  document.body.appendChild(script);
});

const planCards = [
  {
    planKey: 'starter',
    titleKey: 'starter',
    ctaKey: 'chooseStarter',
    activeKey: 'starterActive',
    featured: false,
    prices: {
      monthly: {
        id: 'starter_monthly',
        labelKey: 'monthly',
        regularPrice: '₹399',
        price: '₹249',
        suffix: '/mo',
      },
      yearly: {
        id: 'starter_yearly',
        labelKey: 'yearly',
        regularPrice: '₹3,990',
        price: '₹2,490',
        suffix: '/yr',
      },
    },
    features: [
      'starterDetail',
      'manualWhatsappShare',
      'accountInvoiceHistory',
      'businessProfileAutofill',
      'recurringMandate',
    ],
  },
  {
    planKey: 'basic',
    titleKey: 'basic',
    ctaKey: 'chooseBasic',
    activeKey: 'basicActive',
    featured: true,
    prices: {
      monthly: {
        id: 'basic_monthly',
        labelKey: 'monthly',
        regularPrice: '₹999',
        price: '₹899',
        suffix: '/mo',
      },
      yearly: {
        id: 'basic_yearly',
        labelKey: 'yearly',
        regularPrice: '₹9,990',
        price: '₹8,990',
        suffix: '/yr',
      },
    },
    features: [
      'basicIntelligence',
      'unlimitedInvoices',
      'gstSummaryExport',
      'customerLedger',
      'profitSummary',
    ],
  },
];

const comingSoon = [
  { nameKey: 'razorpayIntegration', icon: CreditCard, noteKey: 'razorpayIntegrationCopy' },
  { nameKey: 'metaIntegration', icon: Megaphone, noteKey: 'metaIntegrationCopy' },
  { nameKey: 'shopifyImport', icon: ShoppingBag, noteKey: 'shopifyImportCopy' },
  { nameKey: 'whatsappAutomation', icon: MessageCircle, noteKey: 'whatsappAutomationCopy' },
  { nameKey: 'codRtoTracker', icon: Truck, noteKey: 'codRtoTrackerCopy' },
  { nameKey: 'returnsRefunds', icon: RotateCcw, noteKey: 'returnsRefundsCopy' },
  { nameKey: 'sevenYearStorage', icon: Archive, noteKey: 'sevenYearStorageCopy' },
];

export default function Pricing() {
  const { user, plan } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const upgrade = async (planKey, packageId) => {
    if (!user) { navigate('/login?redirect=/plan'); return; }
    const card = planCards.find((item) => item.planKey === planKey);
    if (plan === planKey) { toast.success(t(card?.activeKey || 'starterActive')); return; }
    setLoadingPlan(packageId);
    try {
      await loadRazorpay();
      const { data } = await apiClient.post('/payments/razorpay/order', {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (!data.key_id) {
        toast.error(t('checkoutFailed'));
        setLoadingPlan(null);
        return;
      }
      const options = {
        key: data.key_id,
        name: 'BillJaldi',
        description: data.package.name,
        subscription_id: data.subscription?.id,
        prefill: { email: user.email, name: user.name },
        handler: async (response) => {
          await apiClient.post('/payments/razorpay/verify', response);
          toast.success(`${data.package.name} ${t('activated')}`);
          navigate('/dashboard');
        },
        modal: { ondismiss: () => setLoadingPlan(null) },
        theme: { color: '#f97316' },
      };
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (e) {
      console.warn('Checkout failed', formatApiError(e));
      toast.error(t('checkoutFailed'));
      setLoadingPlan(null);
    }
  };

  return (
    <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h1 className="font-heading text-4xl sm:text-5xl font-black text-slate-900 tracking-tight">{t('saveInvoicesForever')}</h1>
      </div>

      <div className="mx-auto max-w-4xl">
        <div className="mx-auto mb-6 max-w-md rounded-full bg-slate-100 p-1 grid grid-cols-2 gap-1" data-testid="billing-cycle-toggle">
          {Object.entries(planCards[0].prices).map(([key, option]) => {
            const active = billingCycle === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setBillingCycle(key)}
                className={`h-11 rounded-full px-3 text-xs font-black uppercase tracking-wider transition-colors ${active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                data-testid={`billing-${key}-toggle`}
              >
                <span>{t(option.labelKey)}</span>
                {key === 'yearly' && <span className={`ml-1 normal-case tracking-normal ${active ? 'text-orange-200' : 'text-orange-600'}`}>{t('savePercent')}</span>}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {planCards.map((card) => {
            const selectedPlan = card.prices[billingCycle];
            const current = plan === card.planKey;
            return (
              <div
                key={card.planKey}
                className={`relative flex flex-col overflow-hidden rounded-2xl border bg-white p-7 text-slate-900 shadow-sm ${card.featured ? 'border-orange-200 ring-2 ring-orange-100' : 'border-slate-200'}`}
                data-testid={`plan-${card.planKey}-card`}
              >
                {card.featured && (
                  <div className="absolute right-4 top-4 rounded-full bg-slate-900 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-white">
                    {t('intelligenceLayer')}
                  </div>
                )}
                <div className="relative mb-6">
                  <div className="text-[10px] tracking-[0.18em] uppercase font-bold mb-2 text-slate-400">{t(card.titleKey)}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-5xl font-black">{selectedPlan.price}</span>
                    <span className="text-sm text-slate-500">{selectedPlan.suffix}</span>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-black text-slate-700 shadow-sm">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">{t('regularPrice')}</span>
                    <span className="text-base line-through decoration-2 decoration-orange-500">{selectedPlan.regularPrice}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-1 relative">
                  {card.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                      <span>{t(feature)}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => upgrade(card.planKey, selectedPlan.id)}
                  disabled={loadingPlan != null || current}
                  variant={card.featured ? 'default' : 'outline'}
                  className={`w-full font-black text-base py-6 ${card.featured ? 'bg-slate-900 text-white hover:bg-slate-800' : ''}`}
                  data-testid={`plan-${card.planKey}-cta`}
                >
                  {current ? t(card.activeKey) : loadingPlan === selectedPlan.id ? t('openingRazorpay') : `${t(card.ctaKey)} ${t(selectedPlan.labelKey)}`}
                </Button>
              </div>
            );
          })}
        </div>
          </div>

      <div className="mt-12 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm" data-testid="coming-soon-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-orange-600">{t('comingSoon')}</div>
            <h2 className="font-heading text-2xl font-black text-slate-900 tracking-tight">{t('whatLaunchesNext')}</h2>
          </div>
          <span className="text-xs font-semibold text-slate-400">{t('julyOnward')}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {comingSoon.map((feature) => (
            <div key={feature.nameKey} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <feature.icon className="w-5 h-5 text-orange-500 mb-3" />
              <div className="font-heading font-bold text-slate-900">{t(feature.nameKey)}</div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t(feature.noteKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
